import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";
import Layout from "../components/Layout";
import { useLang } from "../contexts/LanguageContext";
import { CONTACT } from "../i18n/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contactos = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = t.contactos.required;
    if (!form.email.trim()) e.email = t.contactos.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.contactos.invalidEmail;
    if (!form.message.trim() || form.message.trim().length < 5) e.message = t.contactos.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, locale: lang });
      toast.success(t.contactos.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contactos.error);
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappHref = `https://wa.me/${CONTACT.whatsapp}`;

  const fieldCls =
    "w-full bg-transparent border-b border-[#D5D1C9] focus:border-[#1C1C1C] py-4 text-base placeholder:text-[#999] outline-none transition";

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="uppercase tracking-[0.28em] text-xs text-[#666]">{t.contactos.eyebrow}</span>
          <h1
            className="mt-6 font-light tracking-tight text-4xl sm:text-5xl md:text-6xl max-w-4xl leading-[1.1]"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="contactos-title"
          >
            {t.contactos.title}
          </h1>
          <p className="mt-6 text-lg text-[#444] max-w-2xl leading-relaxed">{t.contactos.subtitle}</p>
        </div>
      </section>

      <section className="pb-28 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
            data-testid="contact-form"
            noValidate
          >
            <div className="md:col-span-1">
              <label className="uppercase tracking-[0.24em] text-[10px] text-[#666]">{t.contactos.name}</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder={t.contactos.placeholderName}
                className={fieldCls}
                data-testid="input-name"
              />
              {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
            </div>
            <div className="md:col-span-1">
              <label className="uppercase tracking-[0.24em] text-[10px] text-[#666]">{t.contactos.email}</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder={t.contactos.placeholderEmail}
                className={fieldCls}
                data-testid="input-email"
              />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>
            <div className="md:col-span-1">
              <label className="uppercase tracking-[0.24em] text-[10px] text-[#666]">{t.contactos.phone}</label>
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder={t.contactos.placeholderPhone}
                className={fieldCls}
                data-testid="input-phone"
              />
            </div>
            <div className="md:col-span-1">
              <label className="uppercase tracking-[0.24em] text-[10px] text-[#666]">{t.contactos.subject}</label>
              <input
                name="subject"
                value={form.subject}
                onChange={onChange}
                placeholder={t.contactos.placeholderSubject}
                className={fieldCls}
                data-testid="input-subject"
              />
            </div>
            <div className="md:col-span-2 mt-4">
              <label className="uppercase tracking-[0.24em] text-[10px] text-[#666]">{t.contactos.message}</label>
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={onChange}
                placeholder={t.contactos.placeholderMessage}
                className={`${fieldCls} resize-none`}
                data-testid="input-message"
              />
              {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
            </div>
            <div className="md:col-span-2 mt-6 flex flex-wrap gap-4 items-center">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-contact-btn"
                className="inline-flex items-center gap-3 bg-[#1C1C1C] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-black transition disabled:opacity-60"
              >
                {submitting ? t.contactos.submitting : t.contactos.submit}
              </button>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-2 border border-[#1C1C1C] text-[#1C1C1C] px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#1C1C1C] hover:text-white transition"
              >
                {t.contactos.whatsappCta}
              </a>
            </div>
          </form>

          {/* Aside */}
          <aside className="lg:col-span-5 bg-[#EBEBE6] p-8 md:p-12 self-start" data-testid="contact-aside">
            <p className="uppercase tracking-[0.24em] text-[11px] text-[#666]">{t.contactos.addressLabel}</p>
            <div className="mt-3 flex gap-3 items-start">
              <MapPin className="w-4 h-4 text-[#555] mt-1 shrink-0" />
              <p className="text-[#1C1C1C] leading-relaxed">{CONTACT.address}</p>
            </div>
            <div className="my-8 border-t border-[#D5D1C9]" />
            <p className="uppercase tracking-[0.24em] text-[11px] text-[#666]">{t.contactos.emailLabel}</p>
            <div className="mt-3 flex gap-3 items-center">
              <Mail className="w-4 h-4 text-[#555]" />
              <a href={`mailto:${CONTACT.email}`} className="text-[#1C1C1C] hover:underline">{CONTACT.email}</a>
            </div>
            <div className="my-8 border-t border-[#D5D1C9]" />
            <p className="uppercase tracking-[0.24em] text-[11px] text-[#666]">{t.contactos.phoneLabel}</p>
            <div className="mt-3 flex gap-3 items-center">
              <Phone className="w-4 h-4 text-[#555]" />
              <a href={`tel:${CONTACT.phoneRaw}`} className="text-[#1C1C1C] hover:underline">{CONTACT.phone}</a>
            </div>

            <div className="mt-10">
              <iframe
                title="map"
                src="https://www.google.com/maps?q=Rua+da+Mec%C3%A2nica+4+C+Ribeira+Grande+Parque+Industrial&output=embed"
                className="w-full h-60 border-0 grayscale"
                loading="lazy"
              />
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
};

export default Contactos;
